import { Col, Form, FormInstance, Row } from 'antd';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// components
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import FormElement from 'components/formElement/formElement';

// services
import { checkPhoneValidation } from 'utils/validators/phone';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';

// models
import { CommitteeMeetingGuest } from '../models/meetings';

interface GuestFormProps {
	form: FormInstance;
	disabled?: boolean;
	onSubmit?(data: CommitteeMeetingGuest): void;
}

const GuestForm: FunctionComponent<GuestFormProps> = ({ form, onSubmit, disabled }) => {
	const { t } = useTranslation();

	const validatePhone = (_: any, value: any) => {
		const number = form.getFieldValue('phone_number');
		if (checkPhoneValidation(form.getFieldValue('phone_country_code'), number)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const validateArabic = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		form.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	return (
		<Form form={form} onFinish={onSubmit} className="my-4">
			<Row gutter={[16, 16]}>
				<Col span={24} md={12}>
					<FormElement
						name="name"
						disabled={disabled}
						type="text"
						label={t('ACTIONS.GUEST_NAME_AR')}
						fullWidth
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
					/>
				</Col>

				<Col span={24} md={12}>
					<FormElement
						name="name_en"
						disabled={disabled}
						type="text"
						label={t('ACTIONS.GUEST_NAME_EN')}
						fullWidth
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
					/>
				</Col>

				<Col span={24} md={12}>
					<Form.Item name="phone_number" rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
						<PhoneInputComponent
							disabled={disabled}
							onChange={onChangePhone}
							label={t('ACTIONS.GUEST_PHONE')}
							userPhoneNumber={form.getFieldValue('phone_number')}
							userDialCode={form.getFieldValue('phone_country_code')}
						/>
					</Form.Item>
					<Form.Item name="phone_country_code" hidden noStyle />
				</Col>

				<Col span={24} md={12}>
					<FormElement
						name="email"
						disabled={disabled}
						label={t('ACTIONS.GUEST_EMAIL')}
						type="email"
						rules={[
							{ required: true, message: t('COMMON.FIELD_REQUIRED') },
							{ type: 'email', message: t('COMMON.EMAIL_INVALID') },
						]}
					/>
				</Col>

				<Col span={24} md={8}>
					<FormElement
						name="position"
						disabled={disabled}
						label={t('ACTIONS.GUEST_POSITION')}
						type="text"
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
					/>
				</Col>

				<Col span={24} md={8}>
					<FormElement
						name="organization"
						disabled={disabled}
						label={t('ACTIONS.GUEST_ORGANIZATION')}
						type="text"
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
					/>
				</Col>

				<Col span={24} md={8}>
					<FormElement
						name="sector"
						disabled={disabled}
						label={t('ACTIONS.GUEST_SECTOR')}
						type="text"
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
					/>
				</Col>
			</Row>
		</Form>
	);
};

export default GuestForm;
